import React, { useRef, useState } from "react";
import axiosInstance from "../../../redux/api";
import swal from "sweetalert";
import { parseProductsFile } from "./parseProductsFile";
import { TaxonomyErrorReport } from "./TaxonomyErrors";
import {
  PATCH_BATCH_SIZE,
  UPDATABLE_FILE_COLUMNS,
  buildFileChange,
} from "./productBulkUpdateMapping";

// "Update from File" — bulk-update existing products from an uploaded
// spreadsheet, hitting the same PATCH admin/products/bulk-update endpoint the
// editable grid uses. Each row is matched to a product by `id` (preferred) or,
// failing that, `barcode` (backend resolves the barcode). Only the recognized,
// non-empty columns in the file are written; blank cells never clear a field.
//
// Flow: PICK (choose file) → READY (preview what will change) → SUBMITTING
// (batched PATCH) → DONE (summary + failures).

const ALLOWED_EXTENSIONS = /\.(xlsx|xls|csv)$/i;
const ALLOWED_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
  "application/csv",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const PHASES = Object.freeze({
  PICK: "pick",
  READY: "ready",
  SUBMITTING: "submitting",
  DONE: "done",
});

const FIELD_LABELS = {
  type: "Type",
  brand: "Brand",
  category: "Category",
  is_b2b: "B2B",
  status: "Status",
};

const unique = (arr) => Array.from(new Set(arr));

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Fold per-row {field, values[]} / {field, value} reports into
// { field: Set(values) } so the preview can list each problem once.
const aggregateByField = (items, valueKey) => {
  const map = {};
  for (const it of items) {
    if (!map[it.field]) map[it.field] = new Set();
    if (valueKey === "values") it.values.forEach((v) => map[it.field].add(v));
    else map[it.field].add(it.value);
  }
  return Object.entries(map).map(([field, set]) => ({
    field,
    values: Array.from(set),
  }));
};

const BulkUpdateFromFile = ({
  dropdownOptions,
  canEditCost = true,
  canEditRegularPrice = true,
  canEditSalePrice = true,
  onClose,
  onComplete,
}) => {
  const [phase, setPhase] = useState(PHASES.PICK);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(null); // { current, total }
  const [result, setResult] = useState(null); // { updatedCount, failed, total }
  const [showFailures, setShowFailures] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setPhase(PHASES.PICK);
    setFile(null);
    setFileError("");
    setPreview(null);
    setProgress(null);
    setResult(null);
    setShowFailures(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFileChange = async (e) => {
    setFileError("");
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (
      !ALLOWED_MIME_TYPES.includes(selected.type) &&
      !ALLOWED_EXTENSIONS.test(selected.name)
    ) {
      setFileError("Only .xlsx, .xls, or .csv files are allowed.");
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setFileError("File size exceeds the 10MB limit.");
      return;
    }
    setFile(selected);
    await parseAndPreview(selected);
  };

  const parseAndPreview = async (selected) => {
    try {
      const { rows, headers } = await parseProductsFile(selected);
      if (!rows || rows.length === 0) {
        setFileError("That file has no data rows.");
        setFile(null);
        return;
      }

      const ctx = {
        dropdownOptions,
        canEditCost,
        canEditRegularPrice,
        canEditSalePrice,
      };
      const built = rows.map((row, i) => ({ rowIndex: i, ...buildFileChange(row, ctx) }));

      const ready = built.filter((b) => b.change);
      const skipped = built.filter((b) => b.skip);

      const columnsTouched = unique(ready.flatMap((b) => b.updatedKeys || []));
      const unresolved = aggregateByField(
        built.flatMap((b) => b.unresolved || []),
        "values"
      );
      const invalid = aggregateByField(
        built.flatMap((b) => b.invalid || []),
        "value"
      );
      const permissionStripped = unique(
        built.flatMap((b) => b.permissionStripped || [])
      );

      const ignoredColumns = (headers || []).filter(
        (h) => h !== "id" && h !== "barcode" && !UPDATABLE_FILE_COLUMNS.has(h)
      );

      setPreview({
        total: rows.length,
        ready,
        skipped,
        columnsTouched,
        unresolved,
        invalid,
        permissionStripped,
        ignoredColumns,
        matchedById: ready.filter((b) => b.matchedBy === "id").length,
        matchedByBarcode: ready.filter((b) => b.matchedBy === "barcode").length,
      });
      setPhase(PHASES.READY);
    } catch (err) {
      console.error("[BulkUpdateFromFile] parse failed", err);
      setFileError(err?.message || "Could not read that file.");
      setFile(null);
    }
  };

  const handleSubmit = async () => {
    if (!preview || preview.ready.length === 0) return;
    setPhase(PHASES.SUBMITTING);
    const changes = preview.ready.map((b) => b.change);
    const successIds = [];
    const failed = [];
    const batches = chunk(changes, PATCH_BATCH_SIZE);
    try {
      for (let i = 0; i < batches.length; i++) {
        setProgress({ current: i + 1, total: batches.length });
        const res = await axiosInstance.patch("admin/products/bulk-update", {
          changes: batches[i],
        });
        const data = res?.data?.response || {};
        successIds.push(...(data.successful_ids || []));
        failed.push(...(data.failed || []));
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        swal({
          title: "No Permission",
          text: "You don't have permission to bulk-edit products.",
          icon: "error",
        });
      } else {
        console.error("[BulkUpdateFromFile] bulk-update failed", err);
        swal({
          title: "Update Failed",
          text: "The server rejected the request. No further rows were sent.",
          icon: "error",
        });
      }
      setProgress(null);
      setPhase(PHASES.READY);
      return;
    }
    setProgress(null);

    const enriched = failed.map((f) => ({
      id: f.id,
      identifier:
        f.identifier || f.name || (f.id != null ? `#${f.id}` : "") || f.barcode || "",
      error: f.error || "Update failed.",
    }));
    const updatedCount = Math.max(successIds.length, changes.length - enriched.length);
    setResult({ updatedCount, failed: enriched, total: changes.length });
    setPhase(PHASES.DONE);
  };

  const finishAndClose = () => {
    // Tell the parent to refresh the grid so the saved values show.
    if (onComplete) onComplete({ updated: result?.updatedCount || 0 });
    onClose();
  };

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-file-earmark-arrow-up me-2 text-primary"></i>
                Update Products from File
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={phase === PHASES.SUBMITTING}
              ></button>
            </div>

            <div className="modal-body">
              {phase === PHASES.PICK && (
                <PickStep
                  inputRef={inputRef}
                  fileError={fileError}
                  onFileChange={handleFileChange}
                />
              )}

              {phase === PHASES.READY && preview && (
                <PreviewStep preview={preview} fileName={file?.name} />
              )}

              {phase === PHASES.SUBMITTING && <SubmittingStep progress={progress} />}

              {phase === PHASES.DONE && result && (
                <DoneStep
                  result={result}
                  skipped={preview?.skipped || []}
                  onViewFailures={() => setShowFailures(true)}
                />
              )}
            </div>

            <div className="modal-footer">
              {phase === PHASES.READY && (
                <>
                  <button type="button" className="btn btn-secondary" onClick={reset}>
                    Choose a different file
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={preview.ready.length === 0}
                  >
                    <i className="bi bi-cloud-arrow-up me-1"></i>
                    Update {preview.ready.length} product
                    {preview.ready.length === 1 ? "" : "s"}
                  </button>
                </>
              )}

              {phase === PHASES.PICK && (
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                  Cancel
                </button>
              )}

              {phase === PHASES.SUBMITTING && (
                <button type="button" className="btn btn-secondary" disabled>
                  Updating…
                </button>
              )}

              {phase === PHASES.DONE && (
                <>
                  <button type="button" className="btn btn-outline-secondary" onClick={reset}>
                    Update another file
                  </button>
                  <button type="button" className="btn btn-primary" onClick={finishAndClose}>
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Refresh list & close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {showFailures && result?.failed?.length > 0 && (
        <TaxonomyErrorReport
          errors={result.failed}
          title={`${result.failed.length} row(s) failed to update`}
          subtitle="These rows were not updated. Fix the listed issues in your file and re-upload only those rows — the rest were saved."
          onClose={() => setShowFailures(false)}
        />
      )}
    </>
  );
};

// --- Steps ----------------------------------------------------------------

function PickStep({ inputRef, fileError, onFileChange }) {
  return (
    <>
      <div className="alert alert-info d-flex align-items-start mb-3" role="alert">
        <i className="bi bi-info-circle-fill me-2 mt-1"></i>
        <div className="small">
          Each row must include an <strong>id</strong> or a <strong>barcode</strong> to
          identify the product to update. Only the columns present in your file
          (with a value) are changed — blank cells are left untouched. Tip: use{" "}
          <strong>Export</strong> to download the current products, edit, and re-upload.
        </div>
      </div>

      <label htmlFor="bulk-update-file" className="form-label">
        Select an Excel / CSV file
      </label>
      <input
        id="bulk-update-file"
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={onFileChange}
        className="form-control"
      />
      {fileError && <div className="text-danger small mt-2">{fileError}</div>}
    </>
  );
}

function StatLine({ value, label, tone = "" }) {
  return (
    <span className="me-3">
      <strong className={tone}>{value}</strong>{" "}
      <span className="text-muted">{label}</span>
    </span>
  );
}

function PreviewStep({ preview, fileName }) {
  const {
    total,
    ready,
    skipped,
    columnsTouched,
    unresolved,
    invalid,
    permissionStripped,
    ignoredColumns,
    matchedById,
    matchedByBarcode,
  } = preview;

  return (
    <>
      <p className="mb-2">
        <i className="bi bi-file-earmark-text me-2 text-muted"></i>
        {fileName} — <strong>{total}</strong> row{total === 1 ? "" : "s"} read.
      </p>

      <div className="d-flex flex-wrap mb-3">
        <StatLine value={ready.length} label="will update" tone="text-success" />
        {skipped.length > 0 && (
          <StatLine value={skipped.length} label="skipped" tone="text-danger" />
        )}
        {matchedById > 0 && <StatLine value={matchedById} label="matched by id" />}
        {matchedByBarcode > 0 && (
          <StatLine value={matchedByBarcode} label="matched by barcode" />
        )}
      </div>

      {ready.length > 0 && columnsTouched.length > 0 && (
        <div className="mb-3">
          <div className="small text-muted mb-1">Columns that will be updated:</div>
          <div className="d-flex flex-wrap gap-1">
            {columnsTouched.map((c) => (
              <span key={c} className="badge bg-light text-dark border">
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {matchedByBarcode > 0 && (
        <div className="alert alert-warning small py-2 mb-3" role="alert">
          <i className="bi bi-upc-scan me-1"></i>
          {matchedByBarcode} row{matchedByBarcode === 1 ? "" : "s"} will be matched
          by barcode. This relies on the backend resolving the barcode — if barcode
          matching isn't enabled yet, those rows will come back as failures.
        </div>
      )}

      {skipped.length > 0 && (
        <WarnBlock
          icon="bi-exclamation-octagon"
          title={`${skipped.length} row(s) will be skipped`}
        >
          Rows missing both an id and a barcode (or with no updatable columns) can't
          be matched to a product.
        </WarnBlock>
      )}

      {unresolved.length > 0 && (
        <WarnBlock icon="bi-question-circle" title="Some taxonomy names didn't match">
          These values weren't found and that field was left unchanged for the
          affected rows (create them first, or fix the spelling):
          <ul className="mb-0 mt-1">
            {unresolved.map((u) => (
              <li key={u.field}>
                <strong>{FIELD_LABELS[u.field] || u.field}:</strong>{" "}
                {u.values.join(", ")}
              </li>
            ))}
          </ul>
        </WarnBlock>
      )}

      {invalid.length > 0 && (
        <WarnBlock icon="bi-exclamation-triangle" title="Some values couldn't be read">
          These cells were left unchanged:
          <ul className="mb-0 mt-1">
            {invalid.map((u) => (
              <li key={u.field}>
                <strong>{FIELD_LABELS[u.field] || u.field}:</strong>{" "}
                {u.values.join(", ")}
              </li>
            ))}
          </ul>
        </WarnBlock>
      )}

      {permissionStripped.length > 0 && (
        <WarnBlock icon="bi-lock" title="Some columns were dropped (no permission)">
          You don't have edit access to: {unique(permissionStripped).join(", ")}.
        </WarnBlock>
      )}

      {ignoredColumns.length > 0 && (
        <div className="small text-muted">
          <i className="bi bi-eye-slash me-1"></i>
          Columns in your file that this update ignores:{" "}
          {ignoredColumns.join(", ")}.
        </div>
      )}
    </>
  );
}

function WarnBlock({ icon, title, children }) {
  return (
    <div
      className="border rounded p-2 mb-3 small"
      style={{ background: "#fffbeb", borderColor: "#fcd34d" }}
    >
      <div className="fw-semibold mb-1">
        <i className={`bi ${icon} text-warning me-1`}></i>
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function SubmittingStep({ progress }) {
  return (
    <div className="d-flex align-items-center gap-3 py-3">
      <span className="spinner-border text-primary" role="status" aria-hidden="true" />
      <div>
        <h6 className="mb-1">Updating products…</h6>
        <p className="text-muted small mb-0">
          {progress
            ? `Sending batch ${progress.current} of ${progress.total}…`
            : "Sending changes…"}
        </p>
      </div>
    </div>
  );
}

function DoneStep({ result, skipped, onViewFailures }) {
  const { updatedCount, failed, total } = result;
  const hasFailures = failed.length > 0;
  return (
    <div className="d-flex align-items-start gap-3">
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: hasFailures ? "#f59e0b" : "#16a34a",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          flexShrink: 0,
        }}
      >
        <i className={`bi ${hasFailures ? "bi-exclamation-triangle-fill" : "bi-check-lg"}`} />
      </div>
      <div className="flex-grow-1">
        <h5 className="mb-1">
          {updatedCount} of {total} product{total === 1 ? "" : "s"} updated.
        </h5>
        {hasFailures && (
          <p className="text-muted mb-2">
            {failed.length} row{failed.length === 1 ? "" : "s"} failed.{" "}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={onViewFailures}
            >
              View details
            </button>
          </p>
        )}
        {skipped.length > 0 && (
          <p className="text-muted small mb-0">
            {skipped.length} row{skipped.length === 1 ? "" : "s"} were skipped (no
            id/barcode or no updatable columns).
          </p>
        )}
      </div>
    </div>
  );
}

export default BulkUpdateFromFile;
